"use client"

import {useState, ReactNode} from "react"
import {Button} from "@/components/ui/button"
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from "@/components/ui/dialog"
import {Textarea} from "@/components/ui/textarea"
import {useToast} from "@/hooks/use-toast";
import {gaId} from "@/lib/analytics/clickTracker";
import {useSession} from "next-auth/react";

type FeedbackFormProps = {
  trigger?: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export default function FeedbackForm({trigger, open: controlledOpen, onOpenChange: controlledOnOpenChange}: FeedbackFormProps = {}) {
  const [satisfaction, setSatisfaction] = useState<number>(0)
  const [feedback, setFeedback] = useState("")
  const [email, setEmail] = useState("")
  const [internalOpen, setInternalOpen] = useState(false)
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen
  const setIsOpen = controlledOnOpenChange || setInternalOpen
  const {toast} = useToast()
  const {data} = useSession()
  const activeEmail = data?.user?.email

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await fetch('https://submit-form.com/kUAn0Di8x', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        satisfaction,
        feedback,
        email: activeEmail ?? email ?? undefined,
      }),
    })
    setIsOpen(false)
    toast({title: "Feedback submitted successfully"})
  }

  const defaultTrigger = (
    <Button
      variant="outline"
      size="sm"
      className="h-8 px-2 py-0 text-xs"
      {...gaId("open_feedback")}
    >
      Submit Feedback
    </Button>
  )

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {(trigger || controlledOpen === undefined) && (
        <DialogTrigger asChild>
          {trigger || defaultTrigger}
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Share Your Feedback</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">How satisfied are you with Devplan?</label>
            <div className="flex justify-between">
              {[
                ["😄", 5, " Very Satisfied"],
                ["🙂", 4, " Satisfied"],
                ["😐", 3, " Neutral"],
                ["🙁", 2, " Dissatisfied"],
                ["😢", 1, "Very Dissatisfied"],
              ].map(([emoji, value, title]) => (
                <Button
                  key={value}
                  type="button"
                  variant="ghost"
                  onClick={() => setSatisfaction(value as number)}
                  className={`flex flex-col items-center p-2 rounded-full transition-all h-auto ${
                    satisfaction === value ? 'bg-blue-50' : 'hover:bg-gray-100'
                  }`}
                >
                  <span className="text-2xl">{emoji}</span>
                  <span className="text-xs mt-1">{title}</span>
                </Button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Please enter your feedback, we appreciate it!</label>
            <Textarea
              required
              maxLength={5000}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Please let us know what is working well, what could be better and what features you’d like to see next. The more direct the better!"
              className="h-32"
            />
            <p className="text-xs text-gray-500 text-right">
              {feedback.length}/5000
            </p>
          </div>
          {!activeEmail && <div className="space-y-2">
            <label className="text-sm font-medium">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>}
          <Button
            type="submit"
            disabled={!feedback}
            className="w-full"
            {...gaId("submit_feedback")}
          >
            Submit Feedback
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
  